import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { ModalV3 } from "react-migration/components/ModalV3";
import { SimplePagination } from "react-migration/components/SimplePagination/SimplePagination";

type FilePreviewProps = {
  blobUrl?: string;
  mime?: string;
  fileName: string;
  url: string;
};

type ModalImagePreviewProps = {
  url: string;
  fileName: string;
  extension?: string | null;
  onDownload: () => void;
  handleClose?: () => void;
  onNavigation?: (direction: "next" | "prev") => void;
  currentItem?: number;
  totalItems?: number;
};

type DownloadTextProps = {
  fileName: string;
  url: string;
  type?: string;
  text?: string;
};

export const DownloadText = ({ fileName = "", url = "", type = "", text = "" }: DownloadTextProps) => {
  return (
    <p className="text-center">
      {text ?? <span>Your browser doesn&apos;t support{` ${type}. `}</span>}
      <a
        href={url}
        download={fileName}
        rel="noopener noreferrer"
        target="_blank"
        className="font-semibold text-blue-500 hover:text-blue-700"
      >
        Download
      </a>{" "}
      the file instead.
    </p>
  );
};

export function FilePreview({ blobUrl, mime = "", fileName = "", url }: FilePreviewProps) {
  if (!blobUrl) {
    return <DownloadText fileName={fileName} url={url} text={"No preview available."} />;
  }
  if (mime.startsWith("image/")) {
    return <img src={blobUrl} alt="Preview" className="w-auto h-full" />;
  }

  if (mime === "application/pdf") {
    return (
      <object
        data={blobUrl}
        type="application/pdf"
        className="block mx-auto max-w-[90vw]! w-[1000px] h-[800px] md:w-[1200px] md:h-full lg:w-[1400px] xl:w-[1600px]"
      >
        <DownloadText fileName={fileName} url={url} type={"inline PDFs"} />
      </object>
    );
  }

  if (mime.startsWith("video/")) {
    return (
      <video controls className="block w-full mx-auto">
        <source src={blobUrl} type={mime} />
        <DownloadText fileName={fileName} url={url} type={"HTML5 video"} />
      </video>
    );
  }

  if (mime.startsWith("audio/")) {
    return (
      <audio controls className="block mx-auto">
        <source src={blobUrl} type={mime} />
        <DownloadText fileName={fileName} url={url} type={"HTML5 audio"} />
      </audio>
    );
  }

  if (mime.startsWith("text/")) {
    return <iframe src={blobUrl} title="Text preview" className="block w-full mx-auto border h-96" />;
  }

  return (
    <div className="text-center">
      <DownloadText fileName={fileName} url={url} text={`No preview available for ${mime}.`} />
    </div>
  );
}
/**
 * NOTE: Apparently the PDFs don't show on smaller screen heights for some reason. This will need to be fixed in the future.
 */
export const ModalImagePreview = ({
  url,
  extension,
  fileName,
  onDownload,
  handleClose,
  onNavigation,
  currentItem,
  totalItems,
}: ModalImagePreviewProps) => {
  const { t } = useTranslation();
  const [mime, setMime] = useState<string>("");
  const [blobUrl, setBlobUrl] = useState<string>(url);
  const [loading, setLoading] = useState(true);

  const extractExt = (u: string) => {
    const clean = u.split("?")[0];
    const parts = clean.split(".");
    return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
  };

  const detectMime = useCallback(async () => {
    const ext = (extension?.replace(/^\./, "") ?? extractExt(url)).toLowerCase();
    switch (ext) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "webp":
        return `image/${ext === "jpg" ? "jpeg" : ext}`;
      case "pdf":
        return "application/pdf";
      case "mp4":
      case "webm":
      case "ogg":
        return `video/${ext}`;
      case "mp3":
      case "wav":
        return `audio/${ext}`;
      case "txt":
        return "text/plain";
      case "html":
        return "text/html";
      default:
        return "application/octet-stream";
    }
  }, [url, extension]);

  const makeBlobUrl = useCallback(
    async (detectedMime: string) => {
      if (detectedMime === "application/pdf") {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const blob = await res.blob();
        const pdfBlob = blob.type === "application/pdf" ? blob : blob.slice(0, blob.size, "application/pdf");
        return URL.createObjectURL(pdfBlob);
      }
      return url;
    },
    [url]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const detected = await detectMime();
        if (cancelled) return;
        setMime(detected);

        const finalUrl = await makeBlobUrl(detected);
        if (cancelled) return;
        setBlobUrl(finalUrl);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectMime, makeBlobUrl]);

  // Add keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowLeft" && currentItem! > 0) {
      onNavigation?.("prev");
    } else if (event.key === "ArrowRight" && currentItem! < (totalItems ?? 1) - 1) {
      onNavigation?.("next");
    }
  };

  return (
    <ModalV3
      onClose={handleClose}
      title={t("workbench.management.tabs.files.modal.preview")}
      description={t("workbench.management.tabs.files.modal.preview.file_name", {
        fileName,
      })}
      fullWidth={true}
      fullHeight={true}
      action={{
        label: t("workbench.management.tabs.files.modal.preview.download_file"),
        onClick: () => onDownload(),
      }}
      withCloseButton={false}
      pagination={
        totalItems && (
          <SimplePagination
            page={currentItem! + 1}
            totalPages={totalItems ?? 1}
            onNextPage={() => onNavigation?.("next")}
            onPreviousPage={() => onNavigation?.("prev")}
          />
        )
      }
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center justify-center w-auto h-full">
        {loading ? (
          <p className="text-center">{t("general.loading")}</p>
        ) : (
          <FilePreview blobUrl={blobUrl} mime={mime} fileName={fileName} url={url} />
        )}
      </div>
    </ModalV3>
  );
};
